// NurturePanel · orchestrator only. State lives in useNurtureSequence,
// UI lives in NurtureEmailCard + NurtureToolbar + StatusPill.
import React from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { Button } from "./ui/button";
import { Loader2, Sparkles, Mail } from "lucide-react";
import { StatusPill, NurtureEmailCard } from "./nurture/NurtureEmailCard";
import NurtureToolbar from "./nurture/NurtureToolbar";
import { useNurtureSequence } from "../hooks/useNurtureSequence";

export default function NurturePanel({ open, lead, onOpenChange, onSequenceChange }) {
  const {
    loading, generating, saving, scheduling,
    sequence, emails, dirty,
    generate, save, schedule, cancel, updateEmail,
  } = useNurtureSequence({ open, lead, onSequenceChange });

  const status = sequence?.status || "none";
  const hasDraft = sequence && emails.length === 3;
  const canEdit = hasDraft && (status === "draft" || status === "scheduled");

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-3xl overflow-y-auto" data-testid="nurture-panel">
        <SheetHeader>
          <SheetTitle className="font-display text-2xl flex items-center gap-3">
            <Mail className="w-5 h-5 text-accent" /> Nurture · <span className="text-accent">{lead?.company}</span>
          </SheetTitle>
          <SheetDescription>
            3-email sequence to <span className="text-foreground">{lead?.email}</span>. Day 0 (now), Day 3, Day 7.
          </SheetDescription>
          {sequence ? <div className="mt-2"><StatusPill status={status} /></div> : null}
        </SheetHeader>

        {loading ? (
          <div className="py-16 flex flex-col items-center text-muted-foreground">
            <Loader2 className="w-6 h-6 animate-spin text-accent" />
            <div className="mt-3 text-sm">Loading…</div>
          </div>
        ) : !hasDraft ? (
          <div className="mt-8 border border-dashed border-border/60 rounded-md p-10 text-center" data-testid="nurture-empty">
            <Sparkles className="w-7 h-7 text-accent mx-auto" />
            <h3 className="font-sans-ui font-semibold mt-4">Generate a 3-email nurture draft</h3>
            <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
              We&rsquo;ll write a thank-you (today), an industry insight tied to gap 2 (Day 3), and a soft booking CTA (Day 7) — each in your voice.
            </p>
            <Button onClick={generate} disabled={generating} data-testid="nurture-generate-btn" className="mt-6 bg-accent text-accent-foreground hover:bg-accent/90 rounded-full">
              {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              {generating ? "Generating…" : "Generate draft"}
            </Button>
          </div>
        ) : (
          <div className="mt-4">
            <div className="flex items-center justify-between flex-wrap gap-2 border-b border-border/60 pb-3 mb-4">
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground">
                {sequence?.updated_at ? `Updated ${new Date(sequence.updated_at).toLocaleString()}` : ""}
                {dirty && <span className="ml-2 text-accent">• unsaved</span>}
              </div>
              <NurtureToolbar
                status={status}
                canEdit={canEdit}
                dirty={dirty}
                generating={generating}
                saving={saving}
                scheduling={scheduling}
                onRegen={generate}
                onRestart={generate}
                onSave={save}
                onSchedule={schedule}
                onCancel={cancel}
              />
            </div>

            <div className="space-y-5">
              {emails.map((e, idx) => (
                <NurtureEmailCard
                  key={e.day}
                  email={e}
                  onChange={(patch) => updateEmail(idx, patch)}
                />
              ))}
            </div>

            <p className="text-xs text-muted-foreground mt-4">
              Emails are sent via Resend, From: your name. The Day 0 email goes out the moment you click Schedule.
            </p>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
